<script lang="ts" setup>
import { ref } from "vue"

const questions = ref([
    {
        title: "À quoi servent mes données ?",
        content: [
            "Elles permettent de mesurer les économies d'énergie réellement obtenues après vos travaux. L'objectif est d'identifier ce qui fonctionne vraiment, pour que les aides publiques futures soient mieux orientées vers les rénovations les plus efficaces.",
        ],
        open: false,
    },
    {
        title: "Quelles données vont être collectées ?",
        content: [
            `
            Nous collectons uniquement les données utiles à l'étude :
            <ul>
                <li>Vos coordonnées (nom, email, téléphone, adresse)</li>
                <li>Les caractéristiques de votre logement (surface, chauffage, nombre d'occupants, DPE...)</li>
                <li>Les informations sur vos travaux financés par les CEE (date, type, montant, kWh cumac...)</li>
                <li>Vos données de consommation d'énergie (Linky, Gazpar…)</li>
            </ul>
            Les consommations sont analysées sur une période d'un an avant et d'un an après vos travaux.
        `,
        ],
        open: false,
    },
    {
        title: "Qui accède à mes données ?",
        content: [
            "Seule Homeys accède à vos données personnelles. Une fois pseudonymisées (vous ne pouvez plus être identifié), elles sont transmises à des partenaires scientifiques dans le cadre de l'étude, pour analyse statistique.",
        ],
        open: false,
    },
    {
        title: "Mes données sont-elles en sécurité ?",
        content: [
            "Oui. Toutes les données sont traitées dans le respect du Règlement Général sur la Protection des Données (RGPD). Vous pouvez vous désinscrire à tout moment en envoyant un email à : ",
            {
                type: "button",
                props: {
                    variant: "link",
                    as: "a",
                    size: "small",
                    class: "button-link",
                    href: "mailto:dpo@homeys.io",
                    label: "dpo@homeys.io",
                },
            },
            ". Notre politique de confidentialité complète est disponible ici : ",
            {
                type: "button",
                props: {
                    variant: "link",
                    as: "a",
                    size: "small",
                    class: "button-link",
                    href: "/politique-de-confidentialite",
                    label: "politique de confidentialité",
                },
            },
        ],
        open: false,
    },
    {
        title: "Pourquoi est-ce important de participer ?",
        content: [
            "Votre participation permet de mieux comprendre l'impact réel des rénovations et d'orienter les futures aides publiques vers les travaux les plus efficaces. Cela contribue à rendre la transition énergétique plus juste et plus performante pour tous.",
        ],
        open: false,
    },
])

function onClickOpen(index: number) {
    questions.value[index].open = !questions.value[index].open
}
</script>

<template>
    <div class="faq-section">
        <h3 class="faq-section__title">Questions fréquentes</h3>
        <div class="faq-section__list">
            <div
                v-for="(question, index) in questions"
                :key="index"
                class="faq-section__item"
            >
                <div
                    class="faq-section__item-header"
                    @click="onClickOpen(index)"
                >
                    <h4 class="faq-section__item-title">
                        {{ question.title }}
                    </h4>
                    <BaseButton
                        :icon="question.open ? 'pi pi-minus' : 'pi pi-plus'"
                        rounded
                        aria-label="Ouvrir"
                        variant="text"
                    />
                </div>

                <div
                    class="faq-section__item-content"
                    :class="{
                        'faq-section__item-content--open': question.open,
                    }"
                >
                    <div>
                        <template
                            v-for="(item, idx) in question.content"
                            :key="idx"
                        >
                            <span
                                v-if="typeof item === 'string'"
                                v-html="item"
                            ></span>
                            <BaseButton v-else v-bind="item.props"></BaseButton>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.faq-section {
    display: flex;
    padding: 8.75rem 0rem 5rem 0rem;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 3.5rem;
    align-self: stretch;

    @media (--large-viewport) {
        /** Pour s'assurer de bien commencer au début du block lors du scrollIntoView */
        scroll-margin-top: 5rem;
    }

    @media (--medium-viewport) {
        padding: 0;
        scroll-margin-top: 0;
    }

    .faq-section__title {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;

        color: #092754;

        font-size: 2rem;
        font-weight: 600;
    }

    .faq-section__list {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 1.5rem;
        align-self: stretch;
    }

    .faq-section__item {
        width: 100%;
        .faq-section__item-header {
            display: flex;
            height: 1.5rem;
            padding: 0.5rem 0rem;
            justify-content: space-between;
            align-items: center;
            align-self: stretch;
            cursor: pointer;

            @media (--medium-viewport) {
                height: auto;
            }

            transition: color 0.3s ease-in-out;
            &:hover {
                .faq-section__item-title {
                    color: #1d6ee7;
                }
            }
        }
        .faq-section__item-title {
            color: #092754;
            font-size: 1.23613rem;
            font-weight: 400;
            line-height: normal;
        }

        .faq-section__item-content {
            max-height: 0;
            height: 100%;
            transition: max-height 0.5s ease-in-out;
            overflow: hidden;

            & > div {
                padding: 0.5rem 0rem;
            }
            &:deep(ul) {
                list-style: disc;
                padding-left: 1.5rem;
                margin-top: 0.5rem;
            }
            &:deep(li) {
                margin-bottom: 0.5rem;
            }
        }
        .faq-section__item-content--open {
            max-height: 10rem;
        }

        padding-bottom: 1.5rem;
        border-bottom: 1px solid #9381ea;
    }
}
.button-link {
    color: #000000;
    padding: 0;
    cursor: pointer;
}
</style>
